import { useState, useEffect } from 'react';
import { History, Clock, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { workflowApi } from '@/lib/api';
import type { WorkflowVersion } from '@shared/types/extended-features';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface VersionIndicatorProps {
  workflowId: string;
  onVersionRestore?: (version: WorkflowVersion) => void;
  refreshTrigger?: number; // Add a trigger prop to force refresh
  currentVersionNumber?: number | null; // Track which version is currently loaded
}

export function VersionIndicator({ workflowId, onVersionRestore, refreshTrigger, currentVersionNumber }: VersionIndicatorProps) {
  const [versions, setVersions] = useState<WorkflowVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Load versions when popover opens
  useEffect(() => {
    if (isOpen && workflowId) {
      loadVersions();
    }
  }, [isOpen, workflowId]);

  // Refresh versions when refreshTrigger changes
  useEffect(() => {
    if (workflowId && refreshTrigger !== undefined) {
      loadVersions();
    }
  }, [refreshTrigger, workflowId]);

  const loadVersions = async () => {
    try {
      setIsLoading(true);
      const data = await workflowApi.getVersions(workflowId);
      setVersions(Array.isArray(data) ? data : []);
    } catch (error) {
      // console.error('Failed to load versions:', error);
      toast.error('Failed to load version history');
      setVersions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoadVersion = async (versionNumber: number) => {
    try {
      const version = await workflowApi.getVersion(workflowId, versionNumber);
      toast.success(`Loaded version ${versionNumber}`);
      setIsOpen(false);
      onVersionRestore?.(version);
    } catch (error) {
      // console.error('Failed to load version:', error);
      toast.error('Failed to load version');
    }
  };

  const latestVersion = versions.length > 0 ? versions[0] : null;
  const displayVersion = currentVersionNumber ?? latestVersion?.versionNumber ?? 1;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 gap-1.5 text-xs font-medium"
        >
          <History className="h-3.5 w-3.5" />
          v{displayVersion}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 pb-2">
          <h4 className="font-semibold text-sm flex items-center gap-2">
            <History className="h-4 w-4" />
            Version History
          </h4>
          <p className="text-xs text-muted-foreground mt-1">
            View and restore previous versions
          </p>
        </div>
        <Separator />
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading versions...
            </div>
          ) : versions.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No versions yet
            </div>
          ) : (
            <div className="p-2">
              {versions.map((version, index) => (
                <div
                  key={version.id}
                  className="group p-3 rounded-md hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant={index === 0 ? 'default' : 'secondary'}
                          className="h-5 px-1.5 text-xs font-mono"
                        >
                          v{version.versionNumber}
                        </Badge>
                        {index === 0 && (
                          <Badge variant="outline" className="h-5 px-1.5 text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        <span>
                          {formatDistanceToNow(new Date(version.createdAt), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </div>
                    {index !== 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleLoadVersion(version.versionNumber)}
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />
                        Load
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
